import { BaseService } from './base.service';
import { ProductService } from './product.service';

export class CategoryService extends BaseService {
  constructor(productService = null) {
    super('categories');
    this._productService = productService;
  }

  // Getter pour productService avec initialisation différée
  get productService() {
    if (!this._productService) {
      this._productService = new ProductService(this);
    }
    return this._productService;
  }

  /**
   * Crée une nouvelle catégorie
   * @param {Object} categoryData - Données de la catégorie
   * @returns {Promise<Object>} - Catégorie créée
   */
  async createCategory(categoryData) {
    try {
      // Création de la catégorie
      const newCategory = await this.create(categoryData);
      return newCategory;
    } catch (error) {
      console.error('Erreur lors de la création de la catégorie:', error);
      throw error;
    }
  }

  /**
   * Met à jour une catégorie et synchronise le nom avec les produits associés
   * @param {number} categoryId - ID de la catégorie
   * @param {Object} updates - Données à mettre à jour
   * @returns {Promise<Object>} - Catégorie mise à jour
   */
  async updateCategory(categoryId, updates) {
    try {
      // 1. Mise à jour de la catégorie
      const updatedCategory = await this.update(categoryId, updates);

      // 2. Si le nom a été modifié, mettre à jour les produits associés
      if (updates.name) {
        await this.productService.updateCategoryNameForAllProducts(categoryId, updates.name);
      }

      return updatedCategory;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la catégorie:', error);
      throw error;
    }
  }

  /**
   * Supprime une catégorie si elle n'a pas de produits associés
   * @param {number} categoryId - ID de la catégorie à supprimer
   * @returns {Promise<boolean>} - true si supprimé, false si impossible
   */
  async deleteCategory(categoryId) {
    try {
      // Vérifier si des produits sont associés à cette catégorie
      const products = await this.productService.getByCategory(categoryId);
      
      if (products.length > 0) {
        throw new Error('Impossible de supprimer la catégorie car elle contient des produits');
      }

      // Si aucun produit n'est associé, on peut supprimer la catégorie
      await this.delete(categoryId);
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de la catégorie:', error);
      throw error;
    }
  }

  /**
   * Récupère une catégorie avec le nombre de produits associés
   * @param {number} categoryId - ID de la catégorie
   * @returns {Promise<Object>} - La catégorie avec le nombre de produits
   */
  async getCategoryWithProductCount(categoryId) {
    try {
      const category = await this.getById(categoryId);
      if (!category) return null;

      const products = await this.productService.getByCategory(categoryId);
      return {
        ...category,
        productCount: products.length
      };
    } catch (error) {
      console.error('Erreur lors de la récupération de la catégorie:', error);
      throw error;
    }
  }

  /**
   * Récupère toutes les catégories avec leur nombre de produits
   * @returns {Promise<Array>} - Liste des catégories avec leur nombre de produits
   */
  async getAllCategoriesWithProductCount() {
    try {
      const categories = await this.getAll();
      const categoriesWithCount = await Promise.all(
        categories.map(async (category) => {
          const products = await this.productService.getByCategory(category.id);
          return { ...category, productCount: products.length };
        })
      );
      return categoriesWithCount;
    } catch (error) {
      console.error('Erreur lors de la récupération des catégories:', error);
      throw error;
    }
  }

  /**
   * Récupère les catégories actives
   * @returns {Promise<Array>} - Liste des catégories actives
   */
  async getActiveCategories() {
    return this.getByField('isActive', true);
  }
} 